import { useState, useMemo } from "react";
import { can } from "../config";
import { apiCall } from "../api";
import { Btn } from "./common/FormElements";
import { Modal } from "./common/Modal";

export default function SpareParts({ spareParts, setSpareParts, user, unitId, refreshSpares }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [usageModal, setUsageModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [usageQty, setUsageQty] = useState(1);
  const [usageMachine, setUsageMachine] = useState("");
  const [usageRemarks, setUsageRemarks] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);

  const filtered = useMemo(() => { const q = search.toLowerCase(); return (spareParts || []).filter(s => !q || s.description.toLowerCase().includes(q) || s.supplier.toLowerCase().includes(q)); }, [spareParts, search]);

  function openAdd() { setForm({ id: `sp-${unitId}-${Date.now()}`, description: "", size: "", grade: "", supplier: "", minQty: 1, maxQty: 2, reorderLevel: 1, availQty: 0 }); setModal("add"); }
  function openEdit(s) { setForm({ ...s }); setModal("edit"); }

  async function save() {
    setSaving(true);
    try {
      if (modal === "edit") { await apiCall("PUT", `/spare-parts/${unitId}/${form.id}`, form); }
      else { await apiCall("POST", `/spare-parts/${unitId}`, { ...form, minQty: Number(form.minQty), maxQty: Number(form.maxQty), reorderLevel: Number(form.reorderLevel), availQty: Number(form.availQty) }); }
      await refreshSpares(); setModal(null);
    } catch (error) { alert("Failed to save spare part"); }
    finally { setSaving(false); }
  }

  async function del(id) {
    if (window.confirm("Delete part?")) {
      try { await apiCall("DELETE", `/spare-parts/${unitId}/${id}`); await refreshSpares(); }
      catch (error) { alert("Failed to delete spare part"); }
    }
  }

  async function recordUsage() {
    if (usageQty <= 0) { alert("Quantity must be positive"); return; }
    setSaving(true);
    try {
      const res = await apiCall("POST", `/spare-parts/${unitId}/${usageModal.part.id}/use`, {
        spare_part_id: usageModal.part.id,
        quantity: Number(usageQty),
        machine_name: usageMachine,
        remarks: usageRemarks,
      });
      alert(res.message || "Usage recorded");
      await refreshSpares();
      setUsageModal(null);
      setUsageQty(1);
      setUsageMachine("");
      setUsageRemarks("");
    } catch (error) { alert("Failed to record usage"); }
    finally { setSaving(false); }
  }

  async function recordRestock() {
    if (usageQty <= 0) { alert("Quantity must be positive"); return; }
    setSaving(true);
    try {
      const res = await apiCall("POST", `/spare-parts/${unitId}/${usageModal.part.id}/restock`, {
        spare_part_id: usageModal.part.id,
        quantity: Number(usageQty),
        remarks: usageRemarks,
      });
      alert(res.message || "Restock recorded");
      await refreshSpares();
      setUsageModal(null);
      setUsageQty(1);
      setUsageRemarks("");
    } catch (error) { alert("Failed to record restock"); }
    finally { setSaving(false); }
  }

  async function openHistory(part) {
    setLoadingHistory(true);
    setHistoryModal({ part, history: [], summary: null });
    try {
      const res = await apiCall("GET", `/spare-parts/${unitId}/${part.id}/usage-history`);
      setHistoryModal({ part, history: res.history || [], summary: res.summary || null });
    } catch (error) {
      alert("Failed to load usage history");
      setHistoryModal(null);
    } finally {
      setLoadingHistory(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="spare-parts">
      {modal && (
        <Modal title={modal === "edit" ? "Edit Spare Part" : "Add Spare Part"} onClose={() => setModal(null)}>
          <div className="grid grid-cols-2 gap-3">
            {[["Item Description", "description", "text"], ["Size (mm)", "size", "text"], ["Grade / Make", "grade", "text"], ["Supplier", "supplier", "text"], ["Min Qty", "minQty", "number"], ["Max Qty", "maxQty", "number"], ["Reorder Level", "reorderLevel", "number"], ["Available Qty", "availQty", "number"]].map(([l, k, t]) => (
              <div key={k}><label className="text-xs text-gray-500">{l}</label><input type={t} className="w-full border rounded-lg px-2 py-1.5 text-sm" value={form[k] || ""} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} /></div>
            ))}
          </div>
          <div className="flex gap-2 mt-4"><Btn onClick={save} disabled={saving} testId="save-spare-btn">{saving ? "Saving..." : "Save"}</Btn><Btn onClick={() => setModal(null)} variant="ghost">Cancel</Btn></div>
        </Modal>
      )}

      {usageModal && (
        <Modal title={usageModal.mode === "use" ? `Use: ${usageModal.part.description}` : `Restock: ${usageModal.part.description}`} onClose={() => setUsageModal(null)}>
          <div className="space-y-3">
            <div className="bg-gray-50 border rounded-lg p-3 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Current Stock</span><span className="font-bold">{usageModal.part.availQty}</span></div>
              <div className="flex justify-between mt-1"><span className="text-gray-500">Reorder Level</span><span>{usageModal.part.reorderLevel}</span></div>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Quantity *</label>
              <input type="number" min="1" className="w-full border rounded-lg px-3 py-1.5 text-sm mt-1" value={usageQty} onChange={e => setUsageQty(e.target.value)} data-testid="usage-qty" />
            </div>
            {usageModal.mode === "use" && (
              <div>
                <label className="text-xs text-gray-500 font-medium">Used on Machine (optional)</label>
                <input className="w-full border rounded-lg px-3 py-1.5 text-sm mt-1" placeholder="e.g. PNEUMATIC PRESS 100T" value={usageMachine} onChange={e => setUsageMachine(e.target.value)} data-testid="usage-machine" />
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 font-medium">Remarks (optional)</label>
              <input className="w-full border rounded-lg px-3 py-1.5 text-sm mt-1" placeholder={usageModal.mode === "use" ? "e.g. Replaced during PM" : "e.g. PO #12345"} value={usageRemarks} onChange={e => setUsageRemarks(e.target.value)} data-testid="usage-remarks" />
            </div>
            {usageModal.mode === "use" && usageQty > usageModal.part.availQty && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-yellow-700">
                Warning: Using {usageQty} but only {usageModal.part.availQty} in stock. Stock will go to 0.
              </div>
            )}
            <div className="flex gap-2">
              <Btn onClick={usageModal.mode === "use" ? recordUsage : recordRestock} disabled={saving} testId="confirm-usage-btn">
                {saving ? "Saving..." : usageModal.mode === "use" ? `Record Usage (-${usageQty})` : `Record Restock (+${usageQty})`}
              </Btn>
              <Btn onClick={() => setUsageModal(null)} variant="ghost">Cancel</Btn>
            </div>
          </div>
        </Modal>
      )}

      {historyModal && (
        <Modal title={`Usage History: ${historyModal.part.description}`} onClose={() => setHistoryModal(null)}>
          <div className="space-y-4">
            {loadingHistory ? (
              <div className="text-center py-8 text-gray-400">Loading...</div>
            ) : (
              <>
                {historyModal.summary && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-orange-700">{historyModal.summary.total_used}</div>
                      <div className="text-xs text-orange-600">Total Used</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-700">{historyModal.summary.total_restocked}</div>
                      <div className="text-xs text-green-600">Total Restocked</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-blue-700">{historyModal.part.availQty}</div>
                      <div className="text-xs text-blue-600">Current Stock</div>
                    </div>
                  </div>
                )}
                {historyModal.summary?.top_machines?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-600 mb-1">Top Machines Using This Part</div>
                    <div className="flex flex-wrap gap-2">
                      {historyModal.summary.top_machines.map((m, i) => (
                        <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">{m.machine_name} ({m.quantity})</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0"><tr>
                      <th className="text-left px-2 py-1.5 font-semibold text-gray-500">Date</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-gray-500">Type</th>
                      <th className="text-center px-2 py-1.5 font-semibold text-gray-500">Qty</th>
                      <th className="text-center px-2 py-1.5 font-semibold text-gray-500">Stock After</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-gray-500">Machine</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-gray-500">By</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-gray-500">Remarks</th>
                    </tr></thead>
                    <tbody>
                      {historyModal.history.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-6 text-gray-400">No usage history yet</td></tr>
                      ) : historyModal.history.map(h => (
                        <tr key={h.id} className="border-b hover:bg-gray-50">
                          <td className="px-2 py-1.5 whitespace-nowrap">{new Date(h.timestamp).toLocaleDateString("en-IN")}</td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${h.type === "usage" ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"}`}>
                              {h.type === "usage" ? "Used" : "Restocked"}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-center font-bold">{h.type === "usage" ? `-${h.quantity}` : `+${h.quantity}`}</td>
                          <td className="px-2 py-1.5 text-center">{h.new_qty}</td>
                          <td className="px-2 py-1.5 text-gray-600">{h.machine_name || "\u2014"}</td>
                          <td className="px-2 py-1.5 text-gray-600">{h.used_by || "\u2014"}</td>
                          <td className="px-2 py-1.5 text-gray-500">{h.remarks || "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <Btn onClick={() => setHistoryModal(null)} variant="ghost">Close</Btn>
          </div>
        </Modal>
      )}

      <div className="flex flex-col sm:flex-row gap-2 justify-between">
        <div><h2 className="font-bold text-gray-800">Common Spare Parts List</h2><p className="text-xs text-gray-500">MNT-F-11 &middot; Update monthly</p></div>
        {can(user, "editSpares") && <Btn onClick={openAdd} testId="add-spare-btn">+ Add Part</Btn>}
      </div>
      <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Search parts..." value={search} onChange={e => setSearch(e.target.value)} data-testid="spare-search" />
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b">{["#", "Item Description", "Size", "Grade", "Supplier", "Min", "Max", "Reorder", "Avail.", "Status", ""].map(h => <th key={h} className="text-left px-3 py-2 text-xs text-gray-500 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-gray-800">{s.description}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{s.size}</td>
                  <td className="px-3 py-2 text-gray-600">{s.grade}</td>
                  <td className="px-3 py-2 text-gray-600">{s.supplier}</td>
                  <td className="px-3 py-2 text-center">{s.minQty}</td>
                  <td className="px-3 py-2 text-center">{s.maxQty}</td>
                  <td className="px-3 py-2 text-center">{s.reorderLevel}</td>
                  <td className="px-3 py-2 text-center font-bold">{s.availQty}</td>
                  <td className="px-3 py-2">{s.availQty <= s.reorderLevel ? <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded font-semibold">LOW</span> : <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded font-semibold">OK</span>}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      <button onClick={() => openHistory(s)} className="text-purple-400 hover:text-purple-600 text-xs" data-testid={`history-spare-${s.id}`}>History</button>
                      {can(user, "useSpares") && <button onClick={() => { setUsageModal({ part: s, mode: "use" }); setUsageQty(1); setUsageMachine(""); setUsageRemarks(""); }} className="text-orange-400 hover:text-orange-600 text-xs" data-testid={`use-spare-${s.id}`}>Use</button>}
                      {can(user, "editSpares") && <>
                        <button onClick={() => { setUsageModal({ part: s, mode: "restock" }); setUsageQty(1); setUsageRemarks(""); }} className="text-green-400 hover:text-green-600 text-xs" data-testid={`restock-spare-${s.id}`}>Restock</button>
                        <button onClick={() => openEdit(s)} className="text-blue-400 hover:text-blue-600 text-xs" data-testid={`edit-spare-${s.id}`}>Edit</button>
                        {can(user, "deleteSpares") && <button onClick={() => del(s.id)} className="text-red-300 hover:text-red-500 text-xs" data-testid={`delete-spare-${s.id}`}>Del</button>}
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
